import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiArrowLeft, FiInstagram } from 'react-icons/fi';
import { AiFillHome } from 'react-icons/ai';
import {
  Header as WelcomeHeader,
  HeaderContainer,
  Logo,
  LogoSJ,
  LogoMate,
  NavLinks,
  NavLink,
  NavHomeIcon,
  NavButtons,
  SignInButton,
  PrimaryButton,
  DashboardButton,
  DashboardBadge,
  Footer,
  FooterContent,
  FooterSection,
  FooterLogo,
  FooterLogoSJ,
  FooterLogoMate,
  FooterTitle,
  FooterLink,
  FooterBottom,
  FooterLine,
  FooterText,
  FooterSocialLinks,
  FooterSocialLink,
} from '../WelcomeScreen/styles';

const Icon = ({ component: Component, ...props }: { component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

const Container = styled.div`
  min-height: calc(100vh - 200px);
  background: var(--bg-primary);
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
  font-family: 'Poppins', sans-serif;
  color: var(--text-gray);
  line-height: 1.8;
  margin-top: 6rem;
  padding-top: 3rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-top: 5rem;
    padding-top: 2rem;
  }
`;

const Title = styled.h1`
  margin-top: 0;
  font-size: 2.5rem;
  font-weight: 900;
  margin: 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
  color: var(--text-dark);

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const LastUpdated = styled.p`
  color: var(--text-muted);
  font-size: 0.9rem;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 1rem;
  margin-top: 2rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubSectionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 1rem;
  color: var(--text-gray);

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const List = styled.ul`
  margin: 1rem 0;
  padding-left: 2rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.75rem;
  line-height: 1.8;
  color: var(--text-gray);
`;

const HighlightBox = styled.div`
  background: rgba(255, 150, 0, 0.1);
  border-left: 4px solid var(--accent-orange);
  padding: 1.5rem;
  border-radius: 8px;
  margin: 2rem 0;
`;

const HighlightText = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text-gray);
  margin: 0;
  font-weight: 600;
`;

const ContactInfo = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 2px solid var(--card-border);
`;

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const handleSignIn = () => {
    navigate('/signin');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleDashboard = () => {
    if (user?.role === 'coach') {
      navigate('/coach/home');
    } else if (user?.role === 'athlete') {
      navigate('/jumper/home');
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const scrollToHome = () => {
    navigate('/');
  };

  const scrollToFeatures = () => {
    if (location.pathname !== '/') {
      navigate('/#features');
    } else {
      navigate('/#features');
    }
  };

  const scrollToFAQ = () => {
    if (location.pathname !== '/') {
      navigate('/#faq');
    } else {
      navigate('/#faq');
    }
  };

  const scrollToPricing = () => {
    navigate('/pricing');
  };

  return (
    <>
      <WelcomeHeader>
        <HeaderContainer>
          <Logo onClick={handleLogoClick}>
            <LogoSJ>sj</LogoSJ>
            <LogoMate>Mate</LogoMate>
          </Logo>
          <NavLinks>
            <NavHomeIcon onClick={scrollToHome} title="Home">
              <Icon component={AiFillHome} />
            </NavHomeIcon>
            <NavLink onClick={scrollToFeatures}>FEATURES</NavLink>
            <NavLink onClick={scrollToFAQ}>FAQ</NavLink>
            <NavLink onClick={scrollToPricing}>PRICING</NavLink>
          </NavLinks>
          <NavButtons>
            {user ? (
              <DashboardButton onClick={handleDashboard}>
                DASHBOARD
                <DashboardBadge>
                  {user.firstName?.charAt(0).toUpperCase() || 'U'}
                </DashboardBadge>
              </DashboardButton>
            ) : (
              <>
                <SignInButton onClick={handleSignIn}>SIGN IN</SignInButton>
                <PrimaryButton onClick={handleSignUp}>START FOR FREE</PrimaryButton>
              </>
            )}
          </NavButtons>
        </HeaderContainer>
      </WelcomeHeader>
      <Container>
        <Title>Privacy Policy</Title>
        <LastUpdated>Last Updated: 26.02.2026</LastUpdated>

      <Section>
        <Paragraph>
          This Privacy Policy describes how sjMate ("we," "our," or "us") collects, uses, and protects your personal 
          information when you use our ski jumping performance tracking platform ("Service"). We are committed to 
          protecting your privacy and ensuring compliance with applicable data protection laws, including the General 
          Data Protection Regulation (GDPR) and other relevant privacy regulations.
        </Paragraph>
        <Paragraph>
          By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy. 
          If you do not agree with our policies and practices, please do not use our Service.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>1. Information We Collect</SectionTitle>
        
        <SubSectionTitle>1.1 Personal Information</SubSectionTitle>
        <Paragraph>
          We collect personal information that you provide directly to us when you:
        </Paragraph>
        <List>
          <ListItem>Create an account (name, email address, password)</ListItem>
          <ListItem>Complete your profile (date of birth, club affiliation, gender)</ListItem>
          <ListItem>Use our Service to track training sessions, competitions, and performance data</ListItem>
          <ListItem>Contact us for support or inquiries</ListItem>
        </List>

        <SubSectionTitle>1.2 Performance and Training Data</SubSectionTitle>
        <Paragraph>
          We collect and store performance-related data including:
        </Paragraph>
        <List>
          <ListItem>Jump distances, speeds, and technical data</ListItem>
          <ListItem>Training session records and notes</ListItem>
          <ListItem>Competition results and rankings</ListItem>
          <ListItem>Equipment usage and assignments</ListItem>
          <ListItem>Weight and height measurements</ListItem>
          <ListItem>Goals and achievements</ListItem>
          <ListItem>Video recordings (stored on your Google Drive, linked through our Service)</ListItem>
        </List>

        <SubSectionTitle>1.3 Automatically Collected Information</SubSectionTitle>
        <Paragraph>
          When you use our Service, we automatically collect certain information, including:
        </Paragraph>
        <List>
          <ListItem>Device information (device type, operating system, browser type)</ListItem>
          <ListItem>Usage data (pages visited, features used, time spent)</ListItem>
          <ListItem>IP address and location data (for security and service optimization)</ListItem>
          <ListItem>Cookies and similar tracking technologies</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>2. How We Use Your Information</SectionTitle>
        <Paragraph>
          We use the collected information for the following purposes:
        </Paragraph>
        <List>
          <ListItem><strong>Service Provision:</strong> To provide, maintain, and improve our Service, including processing your training data, generating analytics, and enabling features</ListItem>
          <ListItem><strong>Account Management:</strong> To create and manage your account, authenticate users, and process subscriptions</ListItem>
          <ListItem><strong>Communication:</strong> To send you service-related notifications, respond to your inquiries, and provide customer support</ListItem>
          <ListItem><strong>Analytics and Insights:</strong> To generate performance analytics, trends, and insights based on your data</ListItem>
          <ListItem><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security threats</ListItem>
          <ListItem><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</ListItem>
          <ListItem><strong>Service Improvement:</strong> To analyze usage patterns and improve our Service's functionality and user experience</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>3. Data Sharing and Disclosure</SectionTitle>
        <Paragraph>
          We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
        </Paragraph>
        
        <SubSectionTitle>3.1 With Your Consent</SubSectionTitle>
        <Paragraph>
          We may share your information when you explicitly consent to such sharing, such as when you choose to share performance data with coaches or team members.
        </Paragraph>

        <SubSectionTitle>3.2 Service Providers</SubSectionTitle>
        <Paragraph>
          We may share information with trusted third-party service providers who assist us in operating our Service, including:
        </Paragraph>
        <List>
          <ListItem>Cloud hosting and storage providers</ListItem>
          <ListItem>Payment processors</ListItem>
          <ListItem>Analytics and monitoring services</ListItem>
          <ListItem>Email service providers</ListItem>
        </List>
        <Paragraph>
          These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
        </Paragraph>

        <SubSectionTitle>3.3 Legal Requirements</SubSectionTitle>
        <Paragraph>
          We may disclose your information if required by law, court order, or governmental authority, or if we believe disclosure is necessary to:
        </Paragraph>
        <List>
          <ListItem>Comply with legal obligations</ListItem>
          <ListItem>Protect our rights, property, or safety</ListItem>
          <ListItem>Protect the rights, property, or safety of our users or others</ListItem>
          <ListItem>Prevent or investigate fraud or security issues</ListItem>
        </List>

        <SubSectionTitle>3.4 Business Transfers</SubSectionTitle>
        <Paragraph>
          In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>4. Data Security</SectionTitle>
        <Paragraph>
          We implement industry-standard security measures to protect your personal information:
        </Paragraph>
        <List>
          <ListItem><strong>Encryption:</strong> We use encryption technologies (SSL/TLS) to protect data in transit and at rest</ListItem>
          <ListItem><strong>Access Controls:</strong> We limit access to personal information to authorized personnel only</ListItem>
          <ListItem><strong>Regular Backups:</strong> We maintain secure, automated backups of your data</ListItem>
          <ListItem><strong>Security Monitoring:</strong> We continuously monitor our systems for security threats and vulnerabilities</ListItem>
          <ListItem><strong>Secure Infrastructure:</strong> We use secure cloud infrastructure with multiple layers of protection</ListItem>
        </List>
        <Paragraph>
          However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>5. Your Rights and Choices</SectionTitle>
        <Paragraph>
          Under applicable data protection laws, including GDPR, you have the following rights:
        </Paragraph>
        
        <SubSectionTitle>5.1 Access and Portability</SubSectionTitle>
        <Paragraph>
          You have the right to access your personal information and receive a copy of your data in a structured, commonly used format.
        </Paragraph>

        <SubSectionTitle>5.2 Correction</SubSectionTitle>
        <Paragraph>
          You can update or correct your personal information at any time through your account settings or by contacting us.
        </Paragraph>

        <SubSectionTitle>5.3 Deletion</SubSectionTitle>
        <Paragraph>
          You have the right to request deletion of your personal information, subject to certain legal and operational requirements.
        </Paragraph>

        <SubSectionTitle>5.4 Restriction of Processing</SubSectionTitle>
        <Paragraph>
          You can request that we restrict the processing of your personal information in certain circumstances.
        </Paragraph>

        <SubSectionTitle>5.5 Objection to Processing</SubSectionTitle>
        <Paragraph>
          You have the right to object to certain types of processing of your personal information.
        </Paragraph>

        <SubSectionTitle>5.6 Withdrawal of Consent</SubSectionTitle>
        <Paragraph>
          If processing is based on consent, you can withdraw your consent at any time, though this may affect your ability to use certain features of the Service.
        </Paragraph>

        <SubSectionTitle>5.7 Data Portability</SubSectionTitle>
        <Paragraph>
          You can request that we transfer your data to another service provider in a machine-readable format.
        </Paragraph>

        <HighlightBox>
          <HighlightText>
            To exercise any of these rights, please contact us at skijumpingmate@gmail.com. We will respond to your request within 30 days, as required by law.
          </HighlightText>
        </HighlightBox>
      </Section>

      <Section>
        <SectionTitle>6. Data Retention</SectionTitle>
        <Paragraph>
          We retain your personal information for as long as necessary to provide our Service and fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
        </Paragraph>
        <Paragraph>
          When you delete your account, we will delete or anonymize your personal information, except where we are required to retain it for legal, regulatory, or legitimate business purposes.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>7. Age Requirement and Children's Privacy</SectionTitle>
        <Paragraph>
          You must be at least 16 years old to create an account and use our Service. We collect your date of birth during registration to verify that you meet this age requirement. We do not knowingly collect personal information from anyone under 16. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately and we will delete it.
        </Paragraph>
        <Paragraph>
          We comply with applicable laws regarding the collection of information from children, including the Children's Online Privacy Protection Act (COPPA) where applicable.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>8. International Data Transfers</SectionTitle>
        <Paragraph>
          Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws than your country.
        </Paragraph>
        <Paragraph>
          We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy, including standard contractual clauses and other mechanisms approved by relevant data protection authorities.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>9. Cookies and Tracking Technologies</SectionTitle>
        <Paragraph>
          We use cookies and similar tracking technologies to enhance your experience, analyze usage, and improve our Service. We use: (1) <strong>Necessary cookies</strong> — required for the Service to function (e.g. authentication); (2) <strong>Analytics cookies</strong> — to understand how the Service is used; (3) <strong>Marketing cookies</strong> — only if you consent, for relevant advertising. You can manage your cookie preferences at any time via{' '}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('sjmate-open-cookie-settings'))}
            style={{ background: 'none', border: 'none', color: 'var(--accent-orange)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit', fontWeight: 600 }}
          >
            Cookie settings
          </button>
          {' '}or in your account. Disabling necessary cookies may affect Service functionality.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>10. Third-Party Services</SectionTitle>
        <Paragraph>
          Our Service may integrate with third-party services, such as Google Drive for video storage. When you use these integrations, your use of third-party services is subject to their respective privacy policies.
        </Paragraph>
        <Paragraph>
          We are not responsible for the privacy practices of third-party services. We encourage you to review their privacy policies.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>11. Changes to This Privacy Policy</SectionTitle>
        <Paragraph>
          We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by:
        </Paragraph>
        <List>
          <ListItem>Posting the updated Privacy Policy on our website</ListItem>
          <ListItem>Sending an email notification to your registered email address</ListItem>
          <ListItem>Displaying a notice within the Service</ListItem>
        </List>
        <Paragraph>
          Your continued use of the Service after changes become effective constitutes acceptance of the updated Privacy Policy.
        </Paragraph>
      </Section>

      <ContactInfo>
        <SectionTitle>Contact Us</SectionTitle>
        <Paragraph>
          If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
        </Paragraph>
        <Paragraph>
          <strong>Email:</strong> skijumpingmate@gmail.com
        </Paragraph>
      </ContactInfo>
      </Container>
      <Footer>
        <FooterContent>
          <FooterSection>
            <FooterLogo>
              <FooterLogoSJ>sj</FooterLogoSJ>
              <FooterLogoMate>Mate</FooterLogoMate>
            </FooterLogo>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>About sjMate</FooterTitle>
              <FooterLink 
                href="/#features" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/#features');
                }}
                style={{ cursor: 'pointer' }}
              >
                Features
              </FooterLink>
              <FooterLink 
                href="/pricing" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/pricing');
                }}
                style={{ cursor: 'pointer' }}
              >
                Pricing
              </FooterLink>
              <FooterLink 
                href="/how-to-start" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/how-to-start');
                }}
                style={{ cursor: 'pointer' }}
              >
                How It Works
              </FooterLink>
            </div>
          </FooterSection>
          <FooterSection>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>Legal</FooterTitle>
              <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink href="/terms-of-service">Terms of Service</FooterLink>
            </div>
          </FooterSection>
          <FooterSection>
            <div style={{ marginTop: '2.5rem' }}>
              <FooterTitle>Contact</FooterTitle>
              <FooterLink href="mailto:skijumpingmate@gmail.com">skijumpingmate@gmail.com</FooterLink>
              <FooterLink href="https://instagram.com/sjmate" target="_blank" rel="noopener noreferrer">
                <Icon component={FiInstagram} style={{ display: 'inline-block', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Instagram
              </FooterLink>
            </div>
          </FooterSection>
        </FooterContent>
        <FooterBottom>
          <FooterText>
            Created and designed in 2026 by <b>z-epic</b>
          </FooterText>
        </FooterBottom>
        <FooterLine />
      </Footer>
    </>
  );
};

export default PrivacyPolicy;

